import React, { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';

import { useUser } from '@/contexts/UserContext';
import AddressIcon from '@/svg-icons/address';

function DesiredPropertySection({ property }) {
  const { address = {}, real_estate_type, property_code } = property;
  const me = useUser();
  const country = me?.profile?.country || 'BR';
  
  const addressFields = useMemo(
    () => [
      { key: 'zipcode', label: 'zipcode' },
      { key: 'street', label: 'street' },
      { key: 'number', label: 'number' },
      { key: 'complement', label: 'complement' },
      { key: 'neighborhood', label: 'neighborhood' },
      { key: 'city', label: 'city' },
      { key: 'state', label: 'state' },
    ].filter(field => country !== 'CO' || field.key !== 'zipcode'),
    [country]
  );

  const monetaryFields = useMemo(
    () => [
      { key: 'rent_value', label: 'rent_value' },
      { key: 'condominium_value', label: 'condominium_value' },
      { key: 'iptu_value', label: 'iptu_value' },
    ],
    []
  );

  return (
    <div className="w-full" data-cy="order-details-address">
      <div className="font-bold text-[calc(6px+0.4vw)] my-[0.6vw] flex items-center">
        <AddressIcon className="text-gray-700 mr-2 w-6 h-6 shrink-0" />
        <FormattedMessage id="desired_property" />
      </div>

      <div className="flex flex-wrap text-[#858585] text-[calc(6px+0.4vw)] my-[1vw] pl-[calc(14px+1.8vw)]">
        {addressFields.map(
          field =>
            address[field.key] && (
              <div key={field.key} className="w-1/3 mb-[1vw]">
                <div className="font-bold">
                  <FormattedMessage id={field.label} />
                </div>
                <span data-cy={`order-details-address-${field.key}`}>
                  {address[field.key]}
                </span>
              </div>
            )
        )}

        {real_estate_type && (
          <div className="w-1/3 mb-[1vw]">
            <div className="font-bold">
              <FormattedMessage id="real_estate_type" />
            </div>
            <span data-cy="order-details-address-real_estate_type">
              <FormattedMessage id={real_estate_type} />
            </span>
          </div>
        )}

        {property_code && (
          <div className="w-1/3 mb-[1vw]">
            <div className="font-bold">
              <FormattedMessage id="property_code" />
            </div>
            <span data-cy="order-details-address-property_code">
              {property_code}
            </span>
          </div>
        )}

        {monetaryFields.map(field => {
          const value = property[field.key];
          if (!value) return null;
          return (
            <div key={field.key} className="w-1/3 mb-[1vw]">
              <div className="font-bold">
                <FormattedMessage id={field.label} />
              </div>
              <span data-cy={`order-details-address-${field.key}`}>
                <FormattedNumber
                  value={value}
                  style="currency" // eslint-disable-line react/style-prop-object
                  currency="BRL"
                />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

DesiredPropertySection.propTypes = {
  property: PropTypes.object.isRequired,
};

export default DesiredPropertySection;
